/**
 * Grassland curing: reading the satellite estimate and recording ground truth.
 *
 * The estimate endpoint answers two questions at once: what the project's
 * curing is now (for the map overlay and the panel) and what a simulation on
 * a given date should use (for the run forms). Only the second carries the
 * hold-forward rule, which is why `for_date` is separate from `estimate`.
 */
import {QueryClient, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {api} from '../utils/api';
import {ApiResponse} from '../types/api';
import type {
    CuringEstimate,
    CuringObservation,
    CuringResponse,
    NewCuringObservation,
} from '../types/curing';

const curingKey = (projectId?: number | string, date?: string) =>
    ['curing', String(projectId ?? ''), date ?? 'today'];

const observationsKey = (projectId?: number | string) =>
    ['curing-observations', String(projectId ?? '')];

/**
 * The project's curing estimate, and what to use for `date`.
 *
 * `date` should be the simulation's own date, not today: a run scheduled for
 * next week wants what the estimate says about next week, and a reconstruction
 * of last month's fire wants what was current then.
 */
export const useCuringEstimate = (
    projectId?: number | string,
    date?: string,
    enabled: boolean = true,
) => {
    return useQuery({
        queryKey: curingKey(projectId, date),
        queryFn: async (): Promise<CuringResponse> => {
            const suffix = date ? `?date=${encodeURIComponent(date)}` : '';
            const response = await api.get<ApiResponse<CuringResponse>>(
                `/api/projects/${projectId}/curing${suffix}`,
            );
            return response.data;
        },
        enabled: Boolean(enabled && projectId),
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });
};

export const useCuringHistory = (projectId?: number | string, enabled = true) => {
    return useQuery({
        queryKey: ['curing-history', String(projectId ?? '')],
        queryFn: async (): Promise<CuringEstimate[]> => {
            const response = await api.get<ApiResponse<{results: CuringEstimate[]}>>(
                `/api/projects/${projectId}/curing/history`,
            );
            return response.data?.results ?? [];
        },
        enabled: Boolean(enabled && projectId),
        refetchOnWindowFocus: false,
    });
};

export const useCuringObservations = (projectId?: number | string, enabled = true) => {
    return useQuery({
        queryKey: observationsKey(projectId),
        queryFn: async (): Promise<CuringObservation[]> => {
            const response = await api.get<ApiResponse<{results: CuringObservation[]}>>(
                `/api/projects/${projectId}/curing/observations`,
            );
            return response.data?.results ?? [];
        },
        enabled: Boolean(enabled && projectId),
        refetchOnWindowFocus: false,
    });
};

/** Ask for a fresh estimate now rather than waiting for the weekly refresh. */
export const useRefreshCuring = (projectId?: number | string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.post(`/api/projects/${projectId}/curing/refresh`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['curing', String(projectId ?? '')]});
        },
    });
};

/**
 * Record what someone actually saw in the paddock.
 *
 * The backend re-estimates straight away, so the correction shows up within
 * minutes instead of at the next weekly refresh.
 */
export const useCreateCuringObservation = (projectId?: number | string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (observation: NewCuringObservation) => {
            return api.post<ApiResponse<CuringObservation>>(
                `/api/projects/${projectId}/curing/observations`,
                observation,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: observationsKey(projectId)});
            queryClient.invalidateQueries({queryKey: ['curing', String(projectId ?? '')]});
        },
    });
};

export const useDeleteCuringObservation = (projectId?: number | string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (observationId: number) => {
            return api.delete(
                `/api/projects/${projectId}/curing/observations/${observationId}`,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: observationsKey(projectId)});
            queryClient.invalidateQueries({queryKey: ['curing', String(projectId ?? '')]});
        },
    });
};

export interface CuringPointReading {
    /** True only where the map actually paints something. */
    available: boolean;
    /** Whether the clicked pixel carries grass fuel, i.e. is part of the overlay. */
    has_grass_fuel?: boolean;
    /** Satellite reading in the open; modelled from the weather under pine. */
    source?: 'satellite' | 'weather_model' | null;
    /** Observations inside the pine that set the modelled level. */
    calibrated_by?: number | null;
    curing: number | null;
    confidence: number | null;
    observed_on: string | null;
    reason?: string | null;
}

/**
 * Curing at one coordinate, for click-to-inspect on the map.
 *
 * Sampled server-side from the stored raster rather than read back out of the
 * rendered overlay: the overlay is colour-mapped, so reversing a pixel colour
 * to a percentage would be ambiguous at the band edges.
 */
export const fetchCuringAtPoint = async (
    projectId: number | string,
    lat: number,
    lon: number,
): Promise<CuringPointReading> => {
    const response = await api.get<ApiResponse<CuringPointReading>>(
        `/api/projects/${projectId}/curing/point?lat=${lat}&lon=${lon}`,
    );
    return response.data;
};

/**
 * Refetch everything that depends on the curing estimate.
 *
 * Call after any vegetation change. The overlay paints grass fuels only, so
 * reclassifying vegetation changes what it shows, and the backend re-renders
 * it before responding. Without this the map keeps the old image until the
 * page is reloaded.
 */
export const invalidateCuring = (
    queryClient: QueryClient,
    projectId?: number | string,
) => {
    const key = String(projectId ?? '');
    queryClient.invalidateQueries({queryKey: ['curing', key]});
    queryClient.invalidateQueries({queryKey: ['curing-history', key]});
    // The listing map overlay and the card badge ride on the projects payload.
    queryClient.invalidateQueries({queryKey: ['projects']});
};

/**
 * Shared colour ramp: green while the grass is growing, gold once it is cured.
 * Used by the click popover, the panel and the project card so the same number
 * always reads the same way.
 *
 * MUST stay in step with CURING_BANDS in the backend's
 * api/shared/curing/constants.py, which colours the map overlay itself. They
 * are the same scale shown two ways: if they drift, the map contradicts the
 * value the popover reports for the pixel under the cursor.
 */
export const curingColour = (pct: number | null | undefined): string => {
    if (pct === null || pct === undefined || Number.isNaN(pct)) return '#9e9e9e';
    if (pct < 25) return '#2e7d32';
    if (pct < 50) return '#7cb342';
    if (pct < 70) return '#c0ca33';
    if (pct < 85) return '#fdd835';
    return '#d4a017';
};

/** Plain-language band, matching the colour ramp. */
export const curingLabel = (pct: number | null | undefined): string => {
    if (pct === null || pct === undefined || Number.isNaN(pct)) return 'Unknown';
    if (pct < 25) return 'Green';
    if (pct < 50) return 'Partly cured';
    if (pct < 70) return 'Moderately cured';
    if (pct < 85) return 'Mostly cured';
    return 'Fully cured';
};
